<template>
  <b-modal
    ref="modal"
    :visible="value"
    :title="title"
    size="xl"
    scrollable
    modal-class="cv-builder"
    no-close-on-backdrop
    no-close-on-esc
    @change="(visible) => $emit('input', visible)"
    @hide="onHide"
  >
    <div v-if="columnsLoading" class="py-4 text-center">
      <cv-spinner class="mr-2" />
      Loading the columns rules can use...
    </div>
    <b-alert v-else-if="columnsError" show variant="danger">{{ columnsError }}</b-alert>

    <template v-else>
      <b-alert v-if="modeNote" show variant="info" class="small">{{ modeNote }}</b-alert>
      <b-alert v-if="readError" show variant="danger">{{ readError }}</b-alert>
      <b-alert v-if="serverError" show variant="danger">{{ serverError }}</b-alert>
      <b-alert v-if="attempted && hasClientErrors" show variant="warning" class="small">
        Fix the highlighted parts before saving.
      </b-alert>

      <!-- The rule's details: name, severity, status, description, where it applies. -->
      <div class="cv-builder__details">
        <b-form-row>
          <b-col md="6">
            <b-form-group label="Name" label-for="cv-rule-name" :invalid-feedback="errors.name" :state="state('name')">
              <b-form-input id="cv-rule-name" v-model="form.name" maxlength="200" :state="state('name')" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Severity" label-for="cv-rule-severity" :invalid-feedback="errors.severity" :state="state('severity')">
              <b-form-select
                id="cv-rule-severity"
                v-model="form.severity"
                :options="severityOptions"
                :state="state('severity')"
                class="cv-builder__select"
              />
            </b-form-group>
          </b-col>
          <b-col v-if="mode !== 'edit'" md="3">
            <b-form-group label="Status" label-for="cv-rule-status">
              <b-form-select id="cv-rule-status" v-model="form.active" :options="statusOptions" class="cv-builder__select" />
            </b-form-group>
          </b-col>
        </b-form-row>

        <b-form-group
          label="Description"
          label-for="cv-rule-description"
          description="Shown on the BDX Validation report."
          :invalid-feedback="errors.description"
          :state="state('description')"
        >
          <b-form-textarea
            id="cv-rule-description"
            v-model="form.description"
            rows="2"
            maxlength="500"
            :state="state('description')"
          />
        </b-form-group>

      </div>

      <!-- Where the rule applies (LIQ-2716): programs by UMR, jurisdictions and TPAs. -->
      <section class="cv-builder__panel" aria-labelledby="cv-rule-applies-to">
        <div class="cv-builder__scope">
          <h4 id="cv-rule-applies-to" class="cv-builder__panel-title mb-0">Applies to</h4>
          <span class="cv-builder__scope-summary">{{ scopeSummary }}</span>
          <cv-spinner v-if="scopeChecking" label="Checking the scope" />
        </div>
        <b-alert v-if="programsError" show variant="danger" class="small">{{ programsError }}</b-alert>
        <b-alert v-if="legacyScopeNote" show variant="info" class="small">{{ legacyScopeNote }}</b-alert>
        <rule-scope-tiles
          :value="form.scope"
          :programs="programs"
          :loading="programsLoading"
          :jurisdiction-options="jurisdictionOptions"
          :tpa-options="tpaOptions"
          :company-name="company.name"
          :errors="errors"
          :warnings="scopeWarnings"
          :override.sync="form.scopeOverride"
          :blocked="scopeBlocked"
          @input="onScopeInput"
        />
      </section>

      <!-- When the rule applies. Both dates optional; dates read MM/DD/YYYY, as across the platform. -->
      <section class="cv-builder__panel" aria-labelledby="cv-rule-period">
        <h4 id="cv-rule-period" class="cv-builder__panel-title">Active period</h4>
        <b-form-row class="align-items-center">
          <b-col md="4">
            <b-form-group
              label="Applies from (optional)"
              label-for="cv-rule-from"
              :invalid-feedback="errors.effective_from"
              :state="state('effective_from')"
            >
              <date-picker
                v-model="form.effectiveFrom"
                type="date"
                :format="DISPLAY_DATE"
                value-type="YYYY-MM-DD"
                placeholder="mm/dd/yyyy"
                :input-attr="{ id: 'cv-rule-from' }"
                class="cv-builder__date"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Applies until (optional)"
              label-for="cv-rule-to"
              :invalid-feedback="errors.effective_to"
              :state="state('effective_to')"
            >
              <date-picker
                v-model="form.effectiveTo"
                type="date"
                :format="DISPLAY_DATE"
                value-type="YYYY-MM-DD"
                placeholder="mm/dd/yyyy"
                :input-attr="{ id: 'cv-rule-to' }"
                class="cv-builder__date"
              />
            </b-form-group>
          </b-col>
          <b-col md="4" class="small text-muted">Leave blank to run with no end date.</b-col>
        </b-form-row>
      </section>

      <b-row class="mt-3">
        <!-- The rule, as a formula - converted from a sentence, or written by hand. -->
        <b-col lg="8">
          <section v-if="plainEnglish" aria-label="Describe the rule" class="cv-builder__describe">
            <label for="cv-rule-sentence" class="cv-builder__heading">Describe the rule</label>
            <p class="small text-muted mb-2">
              Say in a sentence what a claim must satisfy and click Convert. The formula appears below; check it,
              change it if needed, then save.
            </p>
            <div class="cv-builder__describe-row">
              <b-form-textarea
                id="cv-rule-sentence"
                v-model="sentence"
                rows="2"
                max-rows="4"
                maxlength="1000"
                :disabled="drafting"
                placeholder="e.g. Refer to Underwriters must be filled in with Yes or No"
                @keydown.ctrl.enter="draftFromSentence"
              />
              <div class="cv-builder__draft-actions">
                <b-button variant="primary" size="sm" :disabled="!canDraft" @click="draftFromSentence">
                  <cv-spinner v-if="drafting" class="mr-1" label="Converting" />
                  Convert
                </b-button>
                <b-button v-if="drafting" variant="link" size="sm" class="p-0" @click="cancelDraft">Cancel</b-button>
              </div>
            </div>
            <p v-if="draftQuestion" class="cv-builder__draft-question small mb-0 mt-2">{{ draftQuestion }}</p>
            <p v-if="draftNote" class="cv-builder__draft-note small mb-0 mt-2">
              <span class="font-weight-bold">Note:</span> {{ draftNote }}
            </p>
            <b-alert v-if="draftError" show variant="danger" class="small mt-2 mb-0">{{ draftError }}</b-alert>
            <div class="cv-builder__or small text-muted">or write the formula yourself</div>
          </section>

          <label for="cv-rule-formula" class="cv-builder__heading">Write the rule</label>

          <div role="toolbar" aria-label="Insert into the rule" class="cv-builder__inserts">
            <button
              v-for="item in inserts"
              :key="item[0]"
              type="button"
              class="cv-builder__insert"
              @click="insert(item[1])"
            >
              {{ item[0] }}
            </button>
          </div>

          <div class="cv-builder__formula-wrap">
            <textarea
              id="cv-rule-formula"
              ref="formula"
              :value="formula"
              rows="6"
              spellcheck="false"
              class="cv-builder__formula"
              :class="{
                'cv-builder__formula--invalid': formulaInvalid,
                'cv-builder__formula--drafting': drafting,
                'cv-builder__formula--drafted': showDraftedBorder,
                'cv-builder__formula--tracing': !!trace
              }"
              :readonly="drafting"
              :aria-busy="drafting ? 'true' : 'false'"
              placeholder='e.g. Refer_to_Underwriters IN ("Yes", "No")'
              @input="onFormulaInput"
              @keydown="onFormulaKey"
              @click="updateCaret"
              @keyup="updateCaret"
            ></textarea>
            <svg
              v-if="trace"
              :key="trace.key"
              class="cv-builder__trace"
              :width="trace.width"
              :height="trace.height"
              aria-hidden="true"
            >
              <!-- The soft glow just outside the line, as the focused box has. -->
              <rect
                class="cv-builder__trace-glow"
                x="-1.5"
                y="-1.5"
                :width="trace.width + 3"
                :height="trace.height + 3"
                rx="11.5"
                ry="11.5"
                pathLength="1"
              />
              <rect
                x="1"
                y="1"
                :width="trace.width - 2"
                :height="trace.height - 2"
                rx="9"
                ry="9"
                pathLength="1"
                @animationend="trace = null"
              />
            </svg>
            <div v-if="drafting" class="cv-builder__formula-overlay">
              <cv-spinner large label="Converting" />
            </div>
          </div>

          <div v-if="suggestions.length" role="listbox" aria-label="Matching columns" class="cv-builder__suggestions">
            <div class="cv-builder__suggestions-head">
              Columns matching “{{ currentWord }}” · Tab or Enter picks the first · or click one
            </div>
            <button
              v-for="(column, index) in suggestions"
              :key="column.name"
              type="button"
              role="option"
              :aria-selected="index === 0 ? 'true' : 'false'"
              class="cv-builder__suggestion"
              :class="{ 'cv-builder__suggestion--first': index === 0 }"
              @click="pickColumn(column)"
            >
              <span class="cv-builder__suggestion-name">
                <span class="cv-builder__mono font-weight-bold">{{ column.name }}</span>
                <span class="small text-muted">{{ typeLabel(column.data_type) }}</span>
              </span>
              <span class="small">{{ column.description }}</span>
            </button>
          </div>

          <p role="status" class="cv-builder__status" :style="{ color: status.color }">
            <span class="cv-builder__dot" :style="{ background: status.color }"></span>
            <span>
              {{ status.text }}
              <b-button
                v-if="status.canShow"
                size="sm"
                variant="link"
                class="p-0 align-baseline cv-builder__show"
                @click="showProblem"
              >
                Show me
              </b-button>
            </span>
          </p>
          <p v-if="errors.criteria" class="cv-builder__status" style="color: #b42318">{{ errors.criteria }}</p>

          <section aria-label="The rule in words" class="cv-builder__preview">
            <div class="cv-builder__small-heading">Reads as</div>
            <p class="mb-1">{{ previewText || 'The rule in plain English appears here once it’s complete.' }}</p>
            <div class="small text-muted">
              Columns: {{ usedColumns.length ? usedColumns.map(columnLabel).join('; ') : '—' }}
            </div>
            <details v-if="criteria" class="mt-2 small">
              <summary class="cv-builder__summary">What gets saved</summary>
              <pre class="cv-builder__json">{{ criteriaJson }}</pre>
            </details>
          </section>

          <!-- Test rule (CV-5): what the rule would do on the company's latest BDX, before it's saved. -->
          <section aria-label="Test the rule" class="cv-builder__test">
            <div class="cv-builder__test-head">
              <div>
                <div class="cv-builder__small-heading">Test on the latest BDX</div>
                <div class="small text-muted">
                  Checks the rule on {{ company.name }}'s most recently processed BDX. Nothing is saved.
                </div>
              </div>
              <b-button size="sm" variant="outline-primary" :disabled="!testRequest || testing || drafting" @click="runTest">
                <cv-spinner v-if="testing" class="mr-1" label="Testing" />
                {{ testResult ? 'Test again' : 'Test rule' }}
              </b-button>
            </div>
            <p v-if="!testRequest && !testResult" class="small text-muted mt-2 mb-0">{{ testBlockedReason }}</p>
            <b-alert v-if="testError" show variant="danger" class="small mt-2 mb-0">{{ testError }}</b-alert>

            <div v-if="testResult" class="cv-builder__test-result" :class="{ 'cv-builder__test-result--stale': testStale }">
              <p v-if="testStale" class="cv-builder__test-stale small mb-2">
                The rule has changed since this test. Test again to see the new result.
              </p>
              <div class="small text-muted mb-2">
                {{ testFileText }} · {{ testResult.rows_checked }} of its {{ testResult.rows_in_file }} claims are in
                the rule's scope
              </div>
              <div class="cv-builder__test-counts">
                <span class="cv-builder__test-count cv-builder__test-count--pass">
                  <strong>{{ testResult.passed }}</strong> pass
                </span>
                <span class="cv-builder__test-count cv-builder__test-count--fail">
                  <strong>{{ testResult.failed }}</strong> fail
                </span>
                <span v-for="item in testReasons" :key="item.reason" class="small text-muted">
                  {{ reasonLabel(item.reason) }}: {{ item.count }}
                </span>
              </div>
              <b-alert v-for="warning in testResult.warnings" :key="warning" show variant="warning" class="small mt-2 mb-0">
                {{ warning }}
              </b-alert>
              <p v-if="!testResult.rows_checked" class="small mt-2 mb-0">
                None of this file's claims are in the rule's scope, so there was nothing to check.
              </p>
              <div v-if="testResult.examples.length" class="cv-builder__test-table-wrap">
                <table class="table table-sm cv-builder__test-table">
                  <thead>
                    <tr>
                      <th>Claim</th>
                      <th>Program</th>
                      <th v-for="name in testedColumns" :key="name">{{ columnLabel(name) }}</th>
                      <th>Why it fails</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(example, index) in testResult.examples" :key="index">
                      <td>{{ example.claim_reference || '—' }}</td>
                      <td>{{ example.bdx_key || '—' }}</td>
                      <td v-for="name in testedColumns" :key="name" :class="{ 'text-muted': example.values[name] == null }">
                        {{ example.values[name] == null ? '(blank)' : example.values[name] }}
                      </td>
                      <td>{{ reasonLabel(example.reason) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="testResult.failed > testResult.examples.length" class="small text-muted">
                  The first {{ testResult.examples.length }} of {{ testResult.failed }} failing claims.
                </div>
              </div>
              <p class="small text-muted mt-2 mb-0">The rule's “Applies from / until” dates aren't used in a test.</p>
            </div>
          </section>

          <b-form-group
            v-if="needsReason"
            label="Reason for the change"
            label-for="cv-rule-reason"
            class="mt-3"
            :invalid-feedback="errors.reason"
            :state="state('reason')"
          >
            <b-form-textarea id="cv-rule-reason" v-model="form.reason" rows="2" maxlength="450" :state="state('reason')" />
          </b-form-group>
        </b-col>

        <!-- Help: examples, how to write it, the columns. -->
        <b-col lg="4">
          <b-tabs small content-class="pt-2" class="cv-builder__side">
            <b-tab title="Examples">
              <p class="small text-muted mb-2">Click one to put it in the box, then change it.</p>
              <button
                v-for="example in examples"
                :key="example.title"
                type="button"
                class="cv-builder__example"
                @click="useExample(example.formula)"
              >
                <span class="d-block font-weight-bold">{{ example.title }}</span>
                <span class="d-block cv-builder__mono small">{{ example.formula }}</span>
              </button>
            </b-tab>
            <b-tab title="How to write it">
              <div class="small cv-builder__help">
                <p>Columns as the BDX spells them, e.g. <code>Date_Closed</code> (pick from the list as you type).</p>
                <p>Text in quotes: <code>"Yes"</code>. Numbers as they are: <code>0.03</code>. Dates in quotes as <code>"2026-01-31"</code>.</p>
                <p>
                  Checks: <code>IS EMPTY</code>, <code>IS NOT EMPTY</code>, <code>= ≠ &lt; ≤ &gt; ≥</code>,
                  <code>IN ("a", "b")</code>, <code>NOT IN ( … )</code>, <code>CONTAINS "x"</code>,
                  <code>CONTAINS WORD "x"</code>, <code>STARTS WITH "x"</code>, <code>ENDS WITH "x"</code>.
                </p>
                <p>
                  Join with <code>AND</code> / <code>OR</code>, group with <code>( )</code>, <code>NOT ( … )</code>,
                  <code>IF … THEN …</code> to check only some claims.
                </p>
                <p class="mb-0">Sums with <code>+ − × ÷</code>; <code>= … WITHIN 0.03</code> allows rounding.</p>
              </div>
            </b-tab>
            <b-tab title="Columns">
              <rule-columns-panel :columns="columns" :can-edit="isSuperuser" @updated="onColumnUpdated" />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </template>

    <template #modal-footer>
      <b-button variant="link" :disabled="saving" @click="$refs.modal.hide('cancel')">Cancel</b-button>
      <b-button variant="primary" :disabled="saving || drafting || columnsLoading || !!columnsError || !!readError" @click="save">
        <cv-spinner v-if="saving" class="mr-1" label="Saving" />
        {{ saveLabel }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import CustomValidationsService from '@services/customValidationsService'
import { columnLabel, criteriaText } from '@util/criteriaText'
import { columnsUsed } from '@util/criteriaCheck'
import {
  INSERTS,
  formatFormula,
  insertAt,
  insertColumn,
  parseFormula,
  suggestColumns,
  wordBefore
} from '@util/ruleFormula'
import CvSpinner from './CvSpinner.vue'
import RuleColumnsPanel from './RuleColumnsPanel.vue'
import RuleScopeTiles from './RuleScopeTiles.vue'

// The request fields an API error can name, each shown by its own part of the form.
const FORM_FIELDS = [
  'name',
  'description',
  'severity',
  'applicable_keys',
  'programs_mode',
  'program_umrs',
  'program_key_exceptions',
  'jurisdictions_mode',
  'jurisdictions',
  'tpas_mode',
  'tpas',
  'effective_from',
  'effective_to',
  'reason',
  'criteria'
]

// Dates in this window read MM/DD/YYYY, as across the platform (LIQ-2716); they're sent as YYYY-MM-DD.
const DISPLAY_DATE = 'MM/DD/YYYY'

// How long the scope check waits after the last change before asking the server.
const SCOPE_CHECK_DELAY_MS = 400

const TYPE_LABELS = { text: 'Text', numeric: 'Number', date: 'Date', boolean: 'Yes / No' }

const STATUS_COLORS = { ok: '#1f7a4d', incomplete: '#8a4b00', error: '#b42318', empty: '#5b6576' }

const EXAMPLES = [
  {
    title: 'Must be filled in, with allowed values',
    formula: 'Refer_to_Underwriters IS NOT EMPTY AND Refer_to_Underwriters IN ("Yes", "No")'
  },
  {
    title: 'Dates in order',
    formula: 'IF Date_Claim_Opened IS NOT EMPTY AND Date_of_Loss_From IS NOT EMPTY\nTHEN Date_of_Loss_From ≤ Date_Claim_Opened'
  },
  {
    title: 'Amounts add up',
    formula: 'Paid_This_Month_Indemnity + Previously_Paid_Indemnity + Reserve_Indemnity = Total_Incurred_Indemnity WITHIN 0.03'
  },
  {
    title: 'Only check some claims',
    formula: 'IF Claim_Status IN ("Closed", "Re-closed")\nTHEN Date_Closed IS NOT EMPTY AND Reserve_Indemnity = 0'
  },
  { title: 'Text pattern', formula: 'Unique_Market_Reference_UMR STARTS WITH "B"' },
  { title: 'None of these', formula: 'NOT (Claim_Status = "Cancelled" OR Claim_Status = "Withdrawn")' }
]

// Where a rule applies (RuleScopeTiles' value): every part "all" unless narrowed (P2/Q-K).
const blankScope = () => ({
  programsMode: 'all',
  umrs: [],
  keyExceptions: [],
  jurisdictionsMode: 'all',
  jurisdictions: [],
  tpasMode: 'all',
  tpas: []
})

const blankForm = () => ({
  name: '',
  description: '',
  severity: null, // no default - the ticket asks for a deliberate choice
  scope: blankScope(),
  scopeOverride: false, // "Override and save as set"
  active: true,
  effectiveFrom: null,
  effectiveTo: null,
  reason: ''
})

// A rule saved before LIQ-2716 that names its keys (programs_mode null) is shown by UMR once the
// programs have loaded (convertLegacyScope).
const formFromRule = (rule) => ({
  ...blankForm(),
  name: rule.name,
  description: rule.description,
  severity: rule.severity,
  scope: {
    programsMode: rule.programs_mode || (rule.applicable_keys ? 'only' : 'all'),
    umrs: [...(rule.program_umrs || [])],
    keyExceptions: [...(rule.program_key_exceptions || [])],
    jurisdictionsMode: rule.jurisdictions_mode || (rule.jurisdictions ? 'only' : 'all'),
    jurisdictions: [...(rule.jurisdictions || [])],
    tpasMode: rule.tpas_mode || 'all',
    tpas: [...(rule.tpas || [])]
  },
  scopeOverride: !!rule.scope_overridden,
  active: rule.active,
  effectiveFrom: rule.effective_from || null,
  effectiveTo: rule.effective_to || null
})

const umrOf = (program) => String((program && program.umr) || '').trim().toUpperCase()

// "Sedgwick and Crawford" / "3 TPAs".
const namesOrCount = (names, word) =>
  names.length <= 2 ? names.join(' and ') : `${names.length} ${word}s`

// The rule builder (CV-5, LIQ-2272), opened from the rules table to add a custom rule (mode
// "create"), change one that has never checked a BDX file (mode "edit"), or replace one that has
// with a new version (mode "version") - both of those need a reason. The rule is written as a
// formula (src/util/ruleFormula.js) with column autocomplete, checked as it's typed and read back in
// words; the right-hand panel has examples, how to write it, and the columns with what they hold.
// Everything else the ticket lists sits above it: name, severity, status, description, where it
// applies (RuleScopeTiles - programs by UMR, jurisdictions, TPAs, with the server's scope check;
// LIQ-2716) and when (the Active period). Saves through the Custom Validations API; its refusals
// show next to the part they're about. Emits saved({ rule, mode }) and closes.
export default {
  name: 'CustomRuleBuilder',

  components: {
    CvSpinner,
    DatePicker,
    RuleColumnsPanel,
    RuleScopeTiles
  },

  props: {
    // Whether the builder is open (v-model).
    value: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    // The rule to edit or replace; null to create one.
    rule: {
      type: Object,
      default: null
    },
    company: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      DISPLAY_DATE,
      columns: [],
      columnsLoading: false,
      columnsError: null,
      // What the scope chooses from (GET .../programs): the company's programs, every jurisdiction,
      // and the TPAs of those programs.
      programs: [],
      jurisdictionOptions: [],
      tpaOptions: [],
      programsLoaded: false,
      programsLoading: false,
      programsError: null,
      // The scope check (POST .../rules/scope-check): { programs_in_scope, programs_total, warnings }.
      scopeCheck: null,
      scopeChecking: false,
      scopeCheckId: 0,
      scopeTimer: null,
      scopeBlocked: false, // a save was refused for the scope's warnings
      scopeTouched: false, // the user has changed the scope since opening
      legacyKeys: null, // a rule saved before LIQ-2716 that names its keys
      legacyScopeNote: null,
      form: blankForm(),
      formula: '',
      caret: 0,
      readError: null,
      attempted: false,
      saving: false,
      serverError: null,
      fieldErrors: {},
      snapshot: '',
      closeConfirmed: false,
      testing: false,
      testResult: null,
      testError: null,
      testedKey: '',
      testedColumns: [],
      // Converting a sentence to a formula (CV-5) - offered when the columns list says plain English is on.
      plainEnglish: false,
      sentence: '',
      drafting: false,
      draftNote: null,
      draftQuestion: null,
      draftError: null,
      drafted: false,
      draftCancel: null, // the running draft request's axios cancel source
      draftedFormula: null, // the formula as converted - the border stays green until it changes
      trace: null, // { width, height, key } while the green line traces the border
      inserts: INSERTS,
      examples: EXAMPLES,
      severityOptions: [
        { value: null, text: 'Choose…', disabled: true },
        { value: 'critical', text: 'Critical' },
        { value: 'non_critical', text: 'Non-Critical' }
      ],
      statusOptions: [
        { value: true, text: 'Active' },
        { value: false, text: 'Inactive' }
      ]
    }
  },

  computed: {
    title() {
      if (this.mode === 'edit' && this.rule) return `Edit "${this.rule.name}"`
      if (this.mode === 'version' && this.rule) return `New version of "${this.rule.name}"`
      return `Add a custom rule for ${this.company.name}`
    },
    saveLabel() {
      return { edit: 'Save changes', version: 'Save new version' }[this.mode] || 'Save rule'
    },
    modeNote() {
      if (this.mode === 'edit') {
        return "This rule hasn't checked a BDX file yet, so your changes replace it. Once it has, changes need a new version."
      }
      if (this.mode === 'version' && this.rule) {
        const version = this.rule.version
        return `Saving creates version ${version + 1} and switches version ${version} off from the next validation run. Results already recorded keep version ${version}.`
      }
      return null
    },
    needsReason() {
      return this.mode === 'edit' || this.mode === 'version'
    },
    isSuperuser() {
      const user = this.$store.getters.user
      return !!(user && user.is_superuser)
    },
    // --- Where the rule applies (LIQ-2716) ---
    umrOfKey() {
      return Object.fromEntries(this.programs.map((program) => [program.key, umrOf(program)]))
    },
    // The scope as the API takes it. A part set to "all" sends no choices, but the tiles keep them, so
    // switching back restores them; unticked keys only count under a chosen UMR.
    scopeBody() {
      const s = this.form.scope
      const chosen = (mode, list) => (mode === 'all' ? [] : list)
      const umrs = chosen(s.programsMode, s.umrs)
      return {
        programs_mode: s.programsMode,
        program_umrs: umrs,
        program_key_exceptions: s.keyExceptions.filter((key) => umrs.includes(this.umrOfKey[key])),
        jurisdictions_mode: s.jurisdictionsMode,
        jurisdictions: chosen(s.jurisdictionsMode, s.jurisdictions),
        tpas_mode: s.tpasMode,
        tpas: chosen(s.tpasMode, s.tpas)
      }
    },
    scopeKey() {
      return JSON.stringify(this.scopeBody)
    },
    // Every part set to "only these" or "all except" has a choice.
    scopeComplete() {
      const b = this.scopeBody
      return (
        (b.programs_mode === 'all' || b.program_umrs.length > 0) &&
        (b.jurisdictions_mode === 'all' || b.jurisdictions.length > 0) &&
        (b.tpas_mode === 'all' || b.tpas.length > 0)
      )
    },
    scopeWarnings() {
      return (this.scopeCheck && this.scopeCheck.warnings) || []
    },
    // One line next to "Applies to", e.g. "Amlin-led programs except 2 UMRs, US only, 2 TPAs".
    scopeSummary() {
      const s = this.form.scope
      const b = this.scopeBody
      const name = this.company.name
      const umrs = b.program_umrs.length === 1 ? b.program_umrs[0] : `${b.program_umrs.length} UMRs`
      const parts = []
      if (b.programs_mode === 'all' || !b.program_umrs.length) parts.push(`All ${name}-led programs`)
      else parts.push(b.programs_mode === 'only' ? `${umrs} only` : `${name}-led programs except ${umrs}`)
      const unticked = b.program_key_exceptions.length
      if (unticked) parts[0] += ` (${unticked} key${unticked === 1 ? '' : 's'} ${s.programsMode === 'only' ? 'left out' : 'kept in'})`
      if (b.jurisdictions.length) {
        const which = namesOrCount(b.jurisdictions, 'jurisdiction')
        parts.push(b.jurisdictions_mode === 'only' ? `${which} only` : `not ${which}`)
      }
      if (b.tpas.length) {
        const which = b.tpas.length <= 2 ? b.tpas.join(' and ') : `${b.tpas.length} TPAs`
        parts.push(b.tpas_mode === 'only' ? which : `not ${which}`)
      }
      const check = this.scopeCheck
      const count = check
        ? ` · ${check.programs_in_scope.toLocaleString()} of ${check.programs_total.toLocaleString()} programs`
        : ''
      return parts.join(', ') + count
    },
    columnTypes() {
      return Object.fromEntries(this.columns.map((column) => [column.name, column.data_type]))
    },
    parsed() {
      return parseFormula(this.formula, this.columnTypes)
    },
    criteria() {
      return this.parsed.ok ? this.parsed.criteria : null
    },
    criteriaJson() {
      return this.criteria ? JSON.stringify(this.criteria, null, 2) : ''
    },
    previewText() {
      return this.criteria ? criteriaText(this.criteria, { columnTypes: this.columnTypes }) : ''
    },
    usedColumns() {
      return columnsUsed(this.criteria)
    },
    currentWord() {
      const found = wordBefore(this.formula, this.caret)
      return found ? found.word : ''
    },
    suggestions() {
      return suggestColumns(this.currentWord, this.columns)
    },
    status() {
      const p = this.parsed
      if (p.ok) return { text: 'Looks good — this is a complete rule.', color: STATUS_COLORS.ok, canShow: false }
      if (p.kind === 'error') return { text: `Problem: ${p.message}`, color: STATUS_COLORS.error, canShow: p.to > p.from }
      if (p.kind === 'empty' && this.attempted) {
        return { text: 'Write the rule before saving.', color: STATUS_COLORS.error, canShow: false }
      }
      return { text: p.message, color: STATUS_COLORS[p.kind] || STATUS_COLORS.empty, canShow: false }
    },
    formulaInvalid() {
      return this.parsed.kind === 'error' || (this.attempted && !this.parsed.ok)
    },
    clientFormErrors() {
      const f = this.form
      const errors = {}
      if (!f.name.trim()) errors.name = 'Give the rule a name.'
      if (!f.description.trim()) errors.description = 'Describe what the rule checks.'
      if (!f.severity) errors.severity = 'Choose a severity.'
      const b = this.scopeBody
      if (b.programs_mode !== 'all' && !b.program_umrs.length) errors.program_umrs = 'Choose at least one UMR.'
      if (b.jurisdictions_mode !== 'all' && !b.jurisdictions.length) errors.jurisdictions = 'Choose at least one jurisdiction.'
      if (b.tpas_mode !== 'all' && !b.tpas.length) errors.tpas = 'Choose at least one TPA.'
      if (f.effectiveFrom && f.effectiveTo && f.effectiveTo < f.effectiveFrom) {
        errors.effective_to = "The end date can't be before the start date."
      }
      if (this.needsReason && !f.reason.trim()) errors.reason = 'Say why the rule is changing.'
      return errors
    },
    hasClientErrors() {
      return Object.keys(this.clientFormErrors).length > 0 || !this.parsed.ok
    },
    errors() {
      return { ...(this.attempted ? this.clientFormErrors : {}), ...this.fieldErrors }
    },
    // Compared with the snapshot taken on opening, to ask before throwing changes away.
    currentState() {
      return JSON.stringify({ form: this.form, formula: this.formula })
    },

    canDraft() {
      return this.sentence.trim().length >= 3 && !this.drafting
    },
    // Green once the trace is done, while the formula is still as drafted - a mistake still shows red.
    showDraftedBorder() {
      return !!this.draftedFormula && this.formula === this.draftedFormula && !this.trace && !this.formulaInvalid
    },

    // --- Test rule ---
    // What Test rule sends: what the rule checks and where. null until the rule can be tested.
    testRequest() {
      if (!this.criteria || !this.programsLoaded || !this.scopeComplete) return null
      return { criteria: this.criteria, ...this.scopeBody }
    },
    testBlockedReason() {
      if (!this.criteria) return 'Write a complete rule to test it.'
      if (!this.programsLoaded) return `Loading ${this.company.name}'s programs...`
      return 'Under “Applies to”, choose at least one item wherever “Only these” or “All except” is picked, to test it.'
    },
    testStale() {
      return !!this.testResult && this.testedKey !== JSON.stringify(this.testRequest)
    },
    testReasons() {
      return Object.entries(this.testResult ? this.testResult.reasons : {})
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
    },
    testFileText() {
      const file = this.testResult.file
      const name = file.name || `File ${file.id}`
      if (!file.processed_on) return name
      return `${name}, processed ${moment(file.processed_on).format(DISPLAY_DATE)}`
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open) this.init()
        else this.cancelDraft() // closed while drafting: stop waiting for it
      }
    },
    // Whatever the API refused is stale once the rule changes.
    currentState() {
      this.serverError = null
      this.fieldErrors = {}
      this.testError = null
    },
    // A changed scope is checked again; an override ticked for the old warnings doesn't carry over
    // once the user changes the scope (a rule saved with an override keeps it until then).
    scopeKey() {
      this.scopeBlocked = false
      if (this.scopeTouched) this.form.scopeOverride = false
      this.scheduleScopeCheck()
    }
  },

  methods: {
    columnLabel,

    typeLabel(type) {
      return TYPE_LABELS[type] || type
    },

    state(field) {
      return this.errors[field] ? false : null
    },

    async init() {
      this.attempted = false
      this.closeConfirmed = false
      this.readError = null
      this.form = this.rule ? formFromRule(this.rule) : blankForm()
      if (this.mode === 'version') this.form.active = true
      this.formula = this.rule && this.rule.criteria ? formatFormula(this.rule.criteria) : ''
      if (this.rule && !this.rule.criteria) this.readError = "This rule's criteria can't be opened here."
      this.caret = this.formula.length
      this.legacyKeys = this.rule && !this.rule.programs_mode && this.rule.applicable_keys ? [...this.rule.applicable_keys] : null
      this.legacyScopeNote = null
      this.scopeCheck = null
      this.scopeBlocked = false
      this.scopeTouched = false
      this.snapshot = this.currentState
      this.serverError = null
      this.fieldErrors = {}
      this.testResult = null
      this.testError = null
      this.testedKey = ''
      this.testedColumns = []
      this.sentence = ''
      this.draftNote = null
      this.draftQuestion = null
      this.draftError = null
      this.drafted = false
      this.draftedFormula = null
      this.trace = null
      await Promise.all([this.loadColumns(), this.loadPrograms()])
      this.convertLegacyScope()
      this.scheduleScopeCheck(0)
    },

    async loadColumns() {
      this.columnsLoading = true
      this.columnsError = null
      try {
        const { data } = await CustomValidationsService.getRuleColumns()
        this.columns = data.columns || []
        this.plainEnglish = !!data.plain_english
      } catch (err) {
        this.columnsError = this.errorText(err, 'Failed to load the columns rules can use.')
      } finally {
        this.columnsLoading = false
      }
    },

    // What the scope chooses from - loaded once per company.
    async loadPrograms() {
      if (this.programsLoaded || this.programsLoading) return
      this.programsLoading = true
      this.programsError = null
      try {
        const { data } = await CustomValidationsService.getLeadPrograms(this.company.id)
        this.programs = data.programs || []
        this.jurisdictionOptions = data.jurisdictions || []
        this.tpaOptions = data.tpas || []
        this.programsLoaded = true
      } catch (err) {
        this.programsError = this.errorText(err, `Failed to load ${this.company.name}'s programs.`)
      } finally {
        this.programsLoading = false
      }
    },

    // A rule saved before LIQ-2716 named its keys one by one: they're shown under their UMRs, with the
    // other keys under those UMRs unticked. Saving it then covers keys added to those UMRs later.
    convertLegacyScope() {
      if (!this.legacyKeys || !this.programsLoaded) return
      const untouched = this.currentState === this.snapshot
      const named = new Set(this.legacyKeys.map((key) => String(key).trim().toUpperCase()))
      const umrs = [...new Set(this.programs.filter((p) => named.has(p.key) && umrOf(p)).map(umrOf))].sort()
      const keyExceptions = this.programs.filter((p) => umrs.includes(umrOf(p)) && !named.has(p.key)).map((p) => p.key)
      this.form.scope = { ...this.form.scope, programsMode: 'only', umrs, keyExceptions }
      this.legacyScopeNote =
        `This rule was set up for ${named.size} LimeIQ key${named.size === 1 ? '' : 's'}; they're shown under their ` +
        `${umrs.length} UMR${umrs.length === 1 ? '' : 's'}. Saving it also covers keys added to those UMRs later.`
      this.legacyKeys = null
      if (untouched) this.snapshot = this.currentState
    },

    onScopeInput(scope) {
      this.scopeTouched = true
      this.form.scope = scope
    },

    // --- The scope check (LIQ-2716) ---

    // Asks the server once the scope has stopped changing; an incomplete scope isn't checked.
    scheduleScopeCheck(delay = SCOPE_CHECK_DELAY_MS) {
      clearTimeout(this.scopeTimer)
      if (!this.programsLoaded || !this.scopeComplete) {
        this.scopeCheckId += 1 // drop an answer still on its way
        this.scopeCheck = null
        this.scopeChecking = false
        return
      }
      this.scopeTimer = setTimeout(() => this.checkScope(), delay)
    },

    async checkScope() {
      const id = (this.scopeCheckId += 1)
      this.scopeChecking = true
      try {
        const { data } = await CustomValidationsService.checkScope(this.company.id, this.scopeBody)
        if (id === this.scopeCheckId) this.scopeCheck = data
      } catch (err) {
        if (id !== this.scopeCheckId) return
        this.scopeCheck = null
        const data = (err && err.response && err.response.data) || {}
        if (data.field && FORM_FIELDS.includes(data.field)) this.fieldErrors = { [data.field]: data.detail }
      } finally {
        if (id === this.scopeCheckId) this.scopeChecking = false
      }
    },

    // --- The formula box ---

    onFormulaInput(event) {
      this.formula = event.target.value
      this.caret = event.target.selectionStart
    },

    updateCaret(event) {
      this.caret = event.target.selectionStart
    },

    // Tab or Enter takes the first matching column while a column name is being typed.
    onFormulaKey(event) {
      if ((event.key === 'Tab' || event.key === 'Enter') && this.suggestions.length) {
        event.preventDefault()
        this.pickColumn(this.suggestions[0])
      }
    },

    pickColumn(column) {
      const next = insertColumn(this.formula, this.caret, column.name)
      this.setFormula(next.text, next.caret)
    },

    insert(snippet) {
      const next = insertAt(this.formula, this.caret, snippet)
      this.setFormula(next.text, next.caret)
    },

    useExample(formula) {
      this.setFormula(formula, formula.length)
    },

    setFormula(text, caret) {
      this.formula = text
      this.caret = caret
      this.$nextTick(() => {
        const box = this.$refs.formula
        if (!box) return
        box.focus()
        box.setSelectionRange(caret, caret)
      })
    },

    // Selects the part of the formula the problem is about.
    showProblem() {
      const box = this.$refs.formula
      if (!box) return
      box.focus()
      box.setSelectionRange(this.parsed.from, this.parsed.to)
    },

    onColumnUpdated(column) {
      this.columns = this.columns.map((existing) => (existing.name === column.name ? column : existing))
    },

    // --- Converting a sentence to a formula ---

    // The server converts the sentence to a formula; it lands in the formula box like a typed one, so
    // the status line, "Reads as" and Test rule all apply before anything is saved.
    async draftFromSentence() {
      if (!this.canDraft) return
      if (this.formula.trim()) {
        const result = await this.$swal({
          title: 'Replace the formula?',
          text: 'The converted formula replaces what is in the formula box now.',
          showCancelButton: true,
          confirmButtonText: 'REPLACE',
          cancelButtonText: 'KEEP MINE'
        })
        if (!result || !result.value) return
      }
      this.drafting = true
      this.draftNote = null
      this.draftQuestion = null
      this.draftError = null
      const cancel = axios.CancelToken.source()
      this.draftCancel = cancel
      try {
        const { data } = await CustomValidationsService.draftRule(this.company.id, this.sentence.trim(), cancel.token)
        if (data.criteria) {
          const formula = formatFormula(data.criteria)
          this.drafting = false // the formula box is editable again before the caret is placed in it
          this.setFormula(formula, formula.length)
          this.draftNote = data.note || null
          this.drafted = true
          this.showDraftLanded(formula)
        } else {
          this.draftQuestion = data.question || 'Could you say a bit more about what the rule should check?'
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          this.draftError = this.errorText(err, 'The sentence could not be converted. Write the formula yourself instead.')
        }
      } finally {
        if (this.draftCancel === cancel) this.draftCancel = null
        this.drafting = false
      }
    },

    // Stops waiting for the draft; the formula box is left as it was.
    cancelDraft() {
      if (this.draftCancel) this.draftCancel.cancel('Drafting cancelled')
    },

    // The draft landed: the formula box scrolls into view, a green line traces its border once,
    // clockwise from the top-left corner, and the border then stays green until the formula changes.
    // With reduced motion the border just turns green.
    showDraftLanded(formula) {
      this.draftedFormula = formula
      this.$nextTick(() => {
        const box = this.$refs.formula
        if (!box) return
        const reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
        if (box.scrollIntoView) box.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' })
        this.trace = reduceMotion ? null : { width: box.offsetWidth, height: box.offsetHeight, key: Date.now() }
      })
    },

    // --- Test rule ---

    async runTest() {
      const request = this.testRequest
      if (!request) return
      this.testing = true
      this.testError = null
      try {
        const { data } = await CustomValidationsService.testRule(this.company.id, request)
        this.testResult = data
        this.testedKey = JSON.stringify(request)
        this.testedColumns = this.usedColumns.slice()
      } catch (err) {
        this.testError = this.errorText(err, 'The rule could not be tested.')
      } finally {
        this.testing = false
      }
    },

    reasonLabel(reason) {
      if (!reason) return "Doesn't meet the rule"
      return reason.charAt(0).toUpperCase() + reason.slice(1)
    },

    // --- Saving ---

    errorText(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.detail) || fallback
    },

    requestBody() {
      const f = this.form
      const body = {
        name: f.name.trim(),
        description: f.description.trim(),
        severity: f.severity,
        criteria: this.criteria,
        ...this.scopeBody,
        scope_override: f.scopeOverride,
        effective_from: f.effectiveFrom || null,
        effective_to: f.effectiveTo || null
      }
      if (this.mode !== 'edit') body.active = f.active
      if (this.needsReason) body.reason = f.reason.trim()
      if (this.drafted) body.drafted = true // the rule's history says "Converted from a sentence"
      return body
    },

    async save() {
      this.attempted = true
      if (this.hasClientErrors || this.readError) return
      this.saving = true
      try {
        const body = this.requestBody()
        const companyId = this.company.id
        const { data } =
          this.mode === 'edit'
            ? await CustomValidationsService.editRule(companyId, this.rule.id, body)
            : this.mode === 'version'
            ? await CustomValidationsService.supersedeRule(companyId, this.rule.id, body)
            : await CustomValidationsService.createRule(companyId, body)
        this.$emit('saved', { rule: data, mode: this.mode })
        this.closeConfirmed = true
        this.$refs.modal.hide('saved')
      } catch (err) {
        this.showRefusal(err)
      } finally {
        this.saving = false
      }
    },

    // An API refusal next to what it's about: a form field (field), the rule itself, or at the top. A
    // scope with choices that can never take effect shows its warnings under "Applies to" (LIQ-2716).
    showRefusal(err) {
      const data = (err && err.response && err.response.data) || {}
      if (data.code === 'scope_conflict') {
        this.scopeCheck = { ...(this.scopeCheck || { programs_in_scope: 0, programs_total: 0 }), warnings: data.warnings || [] }
        this.scopeBlocked = true
        return
      }
      const message = data.detail || 'The rule could not be saved.'
      const field = data.field ? String(data.field).split('.')[0] : null
      if (field && FORM_FIELDS.includes(field)) this.fieldErrors = { [field]: message }
      else this.serverError = message
    },

    // Closing with unsaved changes, or while a sentence is being converted, asks first. A save closes it too,
    // while still marked as saving - closeConfirmed lets that through, so check it before the saving
    // guard.
    async onHide(event) {
      if (this.closeConfirmed) return
      if (this.saving) {
        event.preventDefault()
        return
      }
      if (this.drafting) {
        event.preventDefault()
        const unsaved = this.currentState !== this.snapshot
        const answer = await this.$swal({
          title: 'Stop converting and close?',
          text: unsaved
            ? "The sentence is still being converted, and the rule hasn't been saved. Closing stops it and discards your changes."
            : 'The sentence is still being converted. Closing stops it.',
          showCancelButton: true,
          confirmButtonText: 'CLOSE',
          cancelButtonText: 'KEEP WAITING'
        })
        if (answer && answer.value) {
          this.cancelDraft()
          this.closeConfirmed = true
          this.$refs.modal.hide('discard')
        }
        return
      }
      if (this.currentState === this.snapshot) return
      event.preventDefault()
      const result = await this.$swal({
        title: 'Discard your changes?',
        text: "The rule hasn't been saved.",
        showCancelButton: true,
        confirmButtonText: 'DISCARD',
        cancelButtonText: 'KEEP EDITING'
      })
      if (result && result.value) {
        this.closeConfirmed = true
        this.$refs.modal.hide('discard')
      }
    }
  }
}
</script>

<style>
/* The theme's input border (gray-300) all but disappears on the builder's pale panels: borders here
   are dark enough to see (3:1 against white), with a clear ring on focus. Red error borders stay. */
.cv-builder .form-control:not(.is-invalid),
.cv-builder .custom-select:not(.is-invalid),
.cv-builder .el-input__inner {
  border-color: #8a94a6;
  background-color: #ffffff;
}

.cv-builder .form-control:not(.is-invalid):focus,
.cv-builder .custom-select:not(.is-invalid):focus,
.cv-builder .el-input__inner:focus {
  border-color: #4353c9;
  box-shadow: 0 0 0 3px rgba(67, 83, 201, 0.2);
}

.cv-builder .custom-control-input:not(:checked) ~ .custom-control-label::before {
  border-color: #8a94a6;
}

.cv-builder__details {
  padding: 14px 16px 4px;
  border: 1px solid #dfe3ec;
  border-radius: 10px;
  background: #fafbfe;
}

/* Room on the right for the dropdown arrow, so it never sits on the text. */
.cv-builder__select {
  padding-right: 2rem;
}

/* "Applies to" and "Active period" (LIQ-2716): panels like the details above them. */
.cv-builder__panel {
  padding: 14px 16px 4px;
  margin-top: 12px;
  border: 1px solid #dfe3ec;
  border-radius: 10px;
  background: #fafbfe;
}

.cv-builder__panel-title {
  margin-bottom: 10px;
  font-size: 1.05rem;
  font-weight: bold;
  color: #1f2a44;
}

.cv-builder__scope {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
}

.cv-builder__scope-summary {
  font-size: 0.875rem;
  color: #2f3a9e;
}

.cv-builder__date {
  width: 100%;
}

.cv-builder__heading {
  display: block;
  margin-bottom: 8px;
  font-size: 1.05rem;
  font-weight: bold;
  color: #1f2a44;
}

.cv-builder__inserts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.cv-builder__insert {
  min-width: 40px;
  min-height: 34px;
  padding: 0 10px;
  font-family: 'JetBrains Mono', SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.8rem;
  font-weight: bold;
  color: #2f3a9e;
  background: #eef0fd;
  border: 1px solid #c9cff5;
  border-radius: 6px;
  cursor: pointer;
}

.cv-builder__mono,
.cv-builder__formula,
.cv-builder__json {
  font-family: 'JetBrains Mono', SFMono-Regular, Menlo, Consolas, monospace;
}

.cv-builder__formula {
  width: 100%;
  padding: 12px 14px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #1f2a44;
  background: #fbfcff;
  border: 2px solid #4353c9;
  border-radius: 10px;
  resize: vertical;
}

.cv-builder__formula--invalid {
  border-color: #b42318;
}

/* With the cursor in it: a soft ring like the builder's other inputs, instead of the browser's black
   focus outline - blue, red on a mistake, green while it holds a converted formula. */
.cv-builder__formula:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(67, 83, 201, 0.2);
}

.cv-builder__formula--invalid:focus {
  box-shadow: 0 0 0 3px rgba(180, 35, 24, 0.2);
}

.cv-builder__formula--drafted:focus {
  box-shadow: 0 0 0 3px rgba(31, 122, 77, 0.2);
}

/* While a sentence is converted: the box is dimmed under a spinner. Once the formula lands, a green
   line traces the border clockwise from the top-left corner (2 s) over a box with no border of its
   own, then the border stays green until the formula changes. */
.cv-builder__formula-wrap {
  position: relative;
}

.cv-builder__formula--drafting {
  opacity: 0.45;
}

.cv-builder__formula-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  color: #2f3a9e;
  background: rgba(247, 248, 254, 0.6);
}

.cv-builder__formula--drafted {
  border-color: #1f7a4d;
}

/* Only the tracing line shows during the lap: the box's own border and the browser's focus ring
   (the box has focus once the draft lands) would hide it. The ring comes back after the lap. */
.cv-builder__formula.cv-builder__formula--tracing,
.cv-builder__formula.cv-builder__formula--tracing:focus {
  border-color: transparent;
  outline: none;
  box-shadow: none;
}

/* The rect's outline starts at its top-left corner and runs clockwise; with pathLength 1, moving
   the dash offset from 1 to 0 draws it from there all the way round. */
.cv-builder__trace {
  position: absolute;
  top: 0;
  left: 0;
  overflow: visible;
  pointer-events: none;
}

.cv-builder__trace rect {
  fill: none;
  stroke: #1f7a4d;
  stroke-width: 2;
  stroke-dasharray: 1;
  stroke-dashoffset: 1;
  animation: cv-builder-trace 2s ease-in-out forwards;
}

/* Drawn with the line: the same soft green ring the focused box shows (3px outside the border). */
.cv-builder__trace .cv-builder__trace-glow {
  stroke: rgba(31, 122, 77, 0.2);
  stroke-width: 3;
}

@keyframes cv-builder-trace {
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cv-builder__trace {
    display: none;
  }
}

.cv-builder__draft-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.cv-builder__suggestions {
  margin-top: 6px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #c9ceda;
  border-radius: 10px;
  box-shadow: 0 10px 28px rgba(31, 42, 68, 0.12);
}

.cv-builder__suggestions-head {
  padding: 2px 10px 6px;
  font-size: 0.75rem;
  color: #5b6576;
}

.cv-builder__suggestion {
  display: block;
  width: 100%;
  padding: 6px 10px;
  text-align: left;
  background: #ffffff;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}

.cv-builder__suggestion--first,
.cv-builder__suggestion:hover {
  background: #eef0fd;
}

.cv-builder__suggestion-name {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.cv-builder__status {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 10px 0 0;
  font-size: 0.9rem;
  font-weight: 600;
}

.cv-builder__dot {
  flex: none;
  width: 10px;
  height: 10px;
  margin-top: 6px;
  border-radius: 50%;
}

.cv-builder__show {
  font-weight: 600;
}

.cv-builder__preview {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #dfe3ec;
  border-radius: 10px;
  background: #fcfcfd;
}

.cv-builder__describe {
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #c9cff5;
  border-radius: 10px;
  background: #f7f8fe;
}

.cv-builder__describe-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px;
}

.cv-builder__describe-row textarea {
  flex: 1 1 320px;
}

.cv-builder__draft-note {
  color: #1f2a44;
}

.cv-builder__draft-question {
  font-weight: 600;
  color: #8a4b00;
}

.cv-builder__or {
  margin-top: 10px;
  text-align: center;
}

.cv-builder__test {
  margin-top: 12px;
  padding: 12px 14px;
  border: 1px solid #dfe3ec;
  border-radius: 10px;
  background: #ffffff;
}

.cv-builder__test-head {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px 12px;
}

.cv-builder__test-result {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #dfe3ec;
}

/* An old result stays readable but clearly isn't for the rule as it is now. */
.cv-builder__test-result--stale > :not(.cv-builder__test-stale) {
  opacity: 0.55;
}

.cv-builder__test-stale {
  font-weight: 600;
  color: #8a4b00;
}

.cv-builder__test-counts {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px 16px;
}

.cv-builder__test-count strong {
  font-size: 1.25rem;
  font-variant-numeric: tabular-nums;
}

.cv-builder__test-count--pass {
  color: #1f7a4d;
}

.cv-builder__test-count--fail {
  color: #b42318;
}

.cv-builder__test-table-wrap {
  margin-top: 10px;
  overflow-x: auto;
}

/* The theme keeps table cells on one line; values and reasons wrap here. */
.cv-builder__test-table th,
.cv-builder__test-table td {
  white-space: normal;
  overflow-wrap: break-word;
  font-size: 0.8rem;
  vertical-align: top;
}

.cv-builder__small-heading {
  margin-bottom: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #5b6576;
}

.cv-builder__summary {
  color: #4353c9;
  cursor: pointer;
}

.cv-builder__json {
  margin: 6px 0 0;
  padding: 8px;
  max-height: 220px;
  overflow: auto;
  font-size: 0.75rem;
  background: #f6f7fb;
  border-radius: 6px;
}

.cv-builder__side {
  position: sticky;
  top: 0;
}

.cv-builder__example {
  display: block;
  width: 100%;
  margin-bottom: 6px;
  padding: 8px 10px;
  text-align: left;
  font-size: 0.85rem;
  color: #1f2a44;
  background: #fafbfe;
  border: 1px solid #e4e7f0;
  border-radius: 8px;
  cursor: pointer;
  white-space: pre-wrap;
}

.cv-builder__help code {
  color: #2f3a9e;
}
</style>
